import { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import MurphyReport from './MurphyReport';
import mapToReport from './mapToReport';

const sanitizeFilename = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '')
  .slice(0, 60);

const todayStamp = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation slightly so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Browser-side hook for rendering a Murphy report PDF and triggering a
 * download. Wraps the @react-pdf renderer + the data mapper.
 */
export default function useDownloadMurphyReport() {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const download = useCallback(async ({ meeting, assessment, variant = 'internal' }) => {
    setGenerating(true);
    setError(null);
    try {
      const report = mapToReport({ meeting, assessment, variant });
      // Generation fetches fonts + the logo over the network; one dropped
      // request fails the whole render (observed in prod 2026-08-21 — fixed
      // by a manual retry). Retry the render itself: a fresh attempt re-issues
      // the failed fetches, and by the second try the assets are usually in
      // the HTTP cache from the mount-time warm-up.
      const MAX_ATTEMPTS = 3;
      let blob;
      for (let attempt = 1; ; attempt += 1) {
        try {
          blob = await pdf(<MurphyReport report={report} />).toBlob();
          break;
        } catch (err) {
          if (attempt >= MAX_ATTEMPTS) throw err;
          console.warn(`Murphy report render attempt ${attempt} failed, retrying:`, err?.message);
          await new Promise(res => setTimeout(res, 400 * attempt));
        }
      }
      const slug = sanitizeFilename(meeting?.meetingTitle || 'murphy-report');
      const filename = `Murphy-Report__${slug}__${variant}__${todayStamp()}.pdf`;
      triggerDownload(blob, filename);
    } catch (err) {
      console.error('Murphy report download failed:', err);
      setError(`${err?.message || 'Failed to generate report'} — usually a network hiccup; try again`);
    } finally {
      setGenerating(false);
    }
  }, []);

  return { download, generating, error };
}
